import { lazy } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { BaseModule } from 'lib/modules';
import { PROCESSOR_VIEW_ROLES } from 'lib/userHelpers';

// Lazy-loaded page components
const CutsheetV2App = lazy(() =>
  import(
    /* webpackChunkName: "cutsheet-v2-app" */ './pages/cutsheet-v2-app'
  ).then((m) => ({ default: m.CutsheetV2App })),
);

const CutsheetCapabilitiesList = lazy(() =>
  import(
    /* webpackChunkName: "cutsheet-capabilities-list" */ './pages/cutsheet-capabilities-list'
  ).then((m) => ({ default: m.CutsheetCapabilitiesList })),
);

const QuestionnaireResponsesPage = lazy(() =>
  import(
    /* webpackChunkName: "questionnaire-responses" */ './pages/questionnaire-responses-page'
  ).then((m) => ({ default: m.QuestionnaireResponsesPage })),
);

// Module configuration
interface CutsheetsV2ModuleConfig {
  /**
   * Base path for the module. Defaults to '/processor/cutsheets-v2'
   */
  basePath?: string;
  /**
   * Whether to include the questionnaire builder route
   */
  includeBuilder?: boolean;
  /**
   * Whether to include legacy redirect from /cutsheets/v2
   */
  includeLegacyRedirect?: boolean;
  /**
   * Module settings from database
   */
  enabled?: boolean;
}

/**
 * Cutsheets V2 Module Implementation
 */
class CutsheetsV2ModuleClass extends BaseModule {
  id = 'CAPABILITIES_V2';
  name = 'Capabilities V2';
  label = 'Cut Capabilities';
  description = 'Advanced cutsheet capabilities and questionnaire management';
  version = '2.0.0';
  /**
   * Creates route objects for the Cutsheets V2 module
   * @param ProtectedRoute
   * @param LazyWrapper
   * @param settings
   * @param ModuleProtectedRoute
   */
  createRoutes = (
    ProtectedRoute: any,
    LazyWrapper: any,
    settings?: CutsheetsV2ModuleConfig,
    ModuleProtectedRoute?: any,
  ): RouteObject[] => {
    const {
      basePath = '/processor/cutsheets-v2',
      includeBuilder = true,
      includeLegacyRedirect = true,
    } = settings || {};

    const routes: RouteObject[] = [];
    const RouteProtector = ModuleProtectedRoute || ProtectedRoute;

    // Legacy redirect
    if (includeLegacyRedirect) {
      routes.push({
        path: '/cutsheets/v2',
        element: <Navigate to={basePath} replace />,
      });
    }

    // Questionnaire builder
    if (includeBuilder) {
      routes.push({
        path: '/cutsheets/questionnaire-builder',
        element: (
          <LazyWrapper>
            <RouteProtector
              component={CutsheetV2App}
              moduleId="CAPABILITIES_V2"
              allowedUserRoles={PROCESSOR_VIEW_ROLES}
              skipLazyWrapper
            />
          </LazyWrapper>
        ),
      });

      routes.push({
        path: '/processor/questionnaire-responses',
        element: (
          <LazyWrapper>
            <RouteProtector
              component={QuestionnaireResponsesPage}
              moduleId="CAPABILITIES_V2"
              allowedUserRoles={PROCESSOR_VIEW_ROLES}
              skipLazyWrapper
            />
          </LazyWrapper>
        ),
      });
    }

    // Main capabilities list. The optional :species segment in the URL is
    // read by CutsheetCapabilitiesList — without it, the page redirects to
    // the first available species so deep links are always species-scoped.
    routes.push({
      path: basePath,
      element: (
        <RouteProtector
          component={CutsheetCapabilitiesList}
          moduleId="CAPABILITIES_V2"
          allowedUserRoles={PROCESSOR_VIEW_ROLES}
        />
      ),
    });

    routes.push({
      path: `${basePath}/:species`,
      element: (
        <RouteProtector
          component={CutsheetCapabilitiesList}
          moduleId="CAPABILITIES_V2"
          allowedUserRoles={PROCESSOR_VIEW_ROLES}
        />
      ),
    });

    return routes;
  };
}

// Export singleton instance
export const CutsheetsV2Module = new CutsheetsV2ModuleClass();

// Legacy export for backwards compatibility
export function createCutsheetsV2Routes(
  ProtectedRoute: any,
  LazyWrapper: any,
  config: CutsheetsV2ModuleConfig = {},
): RouteObject[] {
  return CutsheetsV2Module.createRoutes(ProtectedRoute, LazyWrapper, config);
}
